import React, { useEffect, useRef, useState } from 'react';

import { getFileKind, getExtension } from './fileHelpers';
import { DocumentIcon, PhotoIcon } from '../ui/icons';

// Renders a real image thumbnail for image files, lazily: the blob is only
// fetched once the tile scrolls into view (IntersectionObserver), so an
// applicant with 60 files doesn't trigger 60 downloads on mount. PDFs and
// other types fall back to a typed icon with an extension badge.
const FileThumb = ({ file, loadPreview, className = '', iconClassName = 'h-6 w-6' }) => {
    const kind = getFileKind(file.file_name);
    const ext = getExtension(file.file_name);
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const [url, setUrl] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (kind !== 'image' || !ref.current) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [kind]);

    useEffect(() => {
        let active = true;
        if (kind === 'image' && inView && !url && !failed) {
            loadPreview(file)
                .then((res) => {
                    if (active && res) setUrl(res.url);
                    else if (active) setFailed(true);
                })
                .catch(() => active && setFailed(true));
        }
        return () => {
            active = false;
        };
    }, [kind, inView, url, failed, file, loadPreview]);

    if (kind === 'image' && url && !failed) {
        return (
            <div ref={ref} className={`overflow-hidden bg-[#F5F6FA] ${className}`}>
                <img
                    src={url}
                    alt={file.file_name}
                    loading="lazy"
                    onError={() => setFailed(true)}
                    // Inline styles so the crop beats the legacy global
                    // `img { height: auto }` reset from Bootstrap/Metronic.
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </div>
        );
    }

    const isImage = kind === 'image';
    return (
        <div
            ref={ref}
            className={`flex flex-col items-center justify-center gap-1 bg-[#F5F6FA] text-[#5B3FE0] ${className}`}
        >
            {isImage && !failed ? (
                <span className="inline-flex h-6 w-6 animate-pulse rounded-md bg-[#E5E1FA]" />
            ) : (
                <>
                    {isImage ? (
                        <PhotoIcon className={iconClassName} />
                    ) : (
                        <DocumentIcon className={iconClassName} />
                    )}
                    {ext ? (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">
                            {ext}
                        </span>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default FileThumb;
